/**
 * Flight Data Transformer Utility
 * Converts PNR parsed data to the API format expected by FlightTicket API
 */

import * as constClass from '../constants/agent';

/**
 * Fetch city name from airport code using the backend API
 * @param {string} airportCode - IATA airport code (e.g., "WLG", "AKL")
 * @returns {Promise<string>} City name or airport code if lookup fails
 */
async function getCityNameFromAirportCode(airportCode) {
  if (!airportCode || airportCode === 'XXX') {
    return airportCode || 'Unknown';
  }
  
  try {
    const backend_airports_url = constClass.AUTOCOMPLETE_AIRPORT_BACKEND_URL;
    const response = await fetch(`${backend_airports_url}?q=${airportCode}`);
    const json = await response.json();
    
    // Find exact match by code
    const airport = json.find(apt => apt.code === airportCode);
    if (airport && airport.municipality) {
      return airport.municipality;
    }
    
    // Fallback to airport name if municipality not available
    if (airport && airport.name) {
      return airport.name;
    }
    
    return airportCode; // Fallback to code if no match found
  } catch (error) {
    console.error(`Error fetching city name for ${airportCode}:`, error);
    return airportCode; // Fallback to code on error
  }
}

/**
 * Convert a Date object and time string to ISO format
 * Backend expects format: %Y-%m-%dT%H:%M:%S.%fZ (e.g., "2022-10-26T19:45:00.000Z")
 * This matches the format used by the existing flight search (local_departure/local_arrival)
 * @param {Date} date - Date object
 * @param {string} time - Time string like "19:45"
 * @returns {string} ISO format datetime string with milliseconds and Z suffix
 */
function formatDateTime(date, time) {
  // Always ensure we have a valid date
  let dateObj;
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    // If no valid date, use today
    dateObj = new Date();
  } else {
    // Clone the date to avoid modifying the original
    dateObj = new Date(date);
  }
  
  // Parse time if provided, default to noon
  if (time && typeof time === 'string' && time.includes(':')) {
    const [hours, mins] = time.split(':').map(Number);
    if (!isNaN(hours) && !isNaN(mins) && hours >= 0 && hours < 24 && mins >= 0 && mins < 60) {
      dateObj.setHours(hours, mins, 0, 0);
    } else {
      dateObj.setHours(12, 0, 0, 0);
    }
  } else {
    dateObj.setHours(12, 0, 0, 0);
  }
  
  // Return proper ISO format with milliseconds and Z suffix
  // This matches the format from the flight search API (local_departure/local_arrival)
  const isoString = dateObj.toISOString();
  
  // Ensure we never return an empty string
  if (!isoString || isoString.length === 0) {
    // Fallback to current date/time if something went wrong
    return new Date().toISOString();
  }
  
  return isoString;
}

/**
 * Calculate flight duration in seconds from departure and arrival
 * @param {Date} departureDate 
 * @param {string} departureTime - Time string like "19:45"
 * @param {Date} arrivalDate 
 * @param {string} arrivalTime - Time string like "20:50"
 * @returns {number} Duration in seconds
 */
function calculateDuration(departureDate, departureTime, arrivalDate, arrivalTime) {
  if (!departureDate || !departureTime) {
    return 7200; // Default 2 hours in seconds
  }
  
  try {
    // Parse departure
    const [depHours, depMins] = departureTime.split(':').map(Number);
    const depDate = new Date(departureDate);
    depDate.setHours(depHours, depMins, 0, 0);
    
    // Parse arrival
    let arrDate;
    if (arrivalDate && arrivalTime) {
      const [arrHours, arrMins] = arrivalTime.split(':').map(Number);
      arrDate = new Date(arrivalDate);
      arrDate.setHours(arrHours, arrMins, 0, 0);
      
      // If arrival is before departure, it's next day
      if (arrDate < depDate) {
        arrDate.setDate(arrDate.getDate() + 1);
      }
    } else {
      // Default: 2 hours after departure
      arrDate = new Date(depDate.getTime() + 7200000);
    }
    
    const duration = Math.abs(arrDate - depDate) / 1000; // Convert ms to seconds
    
    // Ensure minimum 1 hour duration
    return Math.max(duration, 3600);
  } catch (e) {
    return 7200; // Default 2 hours
  }
}

/**
 * Map class name to API flight_class value
 * @param {string} className - Class name like "Economy", "Business", "First"
 * @returns {string} API value like "economy", "business", "first"
 */
function mapFlightClass(className) {
  if (!className) return 'economy';
  
  const classMap = {
    'Economy': 'economy',
    'Premium Economy': 'economy',
    'Business': 'business',
    'First': 'first'
  };
  
  return classMap[className] || 'economy';
}

/**
 * Transform PNR parsed data to the format expected by FlightTicket API
 * @param {Object} parsedPNR - Parsed PNR data from pnrParser
 * @param {Object} customerData - Customer information { first_name, last_name, email }
 * @param {boolean} subscribeToNewsletter - Newsletter subscription preference
 * @returns {Promise<Object>} Transformed data ready for FlightTicket API
 */
export async function transformPNRToFlightData(parsedPNR, customerData, subscribeToNewsletter = true) {
  if (!parsedPNR || parsedPNR.error) {
    throw new Error('Invalid PNR data');
  }
  
  const airline = parsedPNR.airline || 'XX';
  const flightNumber = parsedPNR.flightNumber || '000';
  const flightName = `${airline} ${flightNumber}`;
  
  // Format departure time - ensure we always have a valid date
  const departureTime = formatDateTime(parsedPNR.departureDate, parsedPNR.departureTime);
  
  // Format arrival time - use departure date if arrival date is missing
  const arrivalDate = parsedPNR.arrivalDate || parsedPNR.departureDate;
  const arrivalTime = formatDateTime(arrivalDate, parsedPNR.arrivalTime);
  
  // Validate that we have valid ISO strings (should never be empty, but double-check)
  if (!departureTime || departureTime.length === 0) {
    throw new Error('Invalid departure time format');
  }
  if (!arrivalTime || arrivalTime.length === 0) {
    throw new Error('Invalid arrival time format');
  }
  
  const flyDuration = calculateDuration(
    parsedPNR.departureDate,
    parsedPNR.departureTime,
    parsedPNR.arrivalDate,
    parsedPNR.arrivalTime
  );
  
  // Get airport codes
  const flyFrom = parsedPNR.flyFrom || 'XXX';
  const flyTo = parsedPNR.flyTo || 'XXX';
  
  // Fetch city names from airport codes using the backend API
  const [cityFrom, cityTo] = await Promise.all([
    getCityNameFromAirportCode(flyFrom),
    getCityNameFromAirportCode(flyTo)
  ]);
  
  // Create route array - must include local_departure and local_arrival for template
  // The template iterates over route and expects these fields
  const route = [{
    flyFrom: flyFrom,
    flyTo: flyTo,
    cityFrom: cityFrom,
    cityTo: cityTo,
    local_departure: departureTime,  // Required by template
    local_arrival: arrivalTime       // Required by template
  }];
  
  return {
    // Customer data
    first_name: customerData.first_name,
    last_name: customerData.last_name,
    email: customerData.email,
    subscribe_to_newsletter: subscribeToNewsletter,
    
    // Flight data
    airline: airline,
    flight_name: flightName,
    departure_time: departureTime,
    arrival_time: arrivalTime,
    fly_from: flyFrom,
    fly_to: flyTo,
    city_from: cityFrom,
    city_to: cityTo,
    fly_duration: flyDuration,
    route: route,
    price: 0, // Free ticket
    
    // Pro ticket attributes (for future use)
    flight_class: mapFlightClass(parsedPNR.class)
  };
}

/**
 * Validate customer data
 * @param {Object} customerData - { first_name, last_name, email }
 * @returns {Object} { valid: boolean, errors: Object }
 */
export function validateCustomerData(customerData) {
  const errors = {};
  
  if (!customerData.first_name || customerData.first_name.trim().length < 2) {
    errors.first_name = 'First name must be at least 2 characters';
  }
  
  if (!customerData.last_name || customerData.last_name.trim().length < 2) {
    errors.last_name = 'Last name must be at least 2 characters';
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!customerData.email || !emailRegex.test(customerData.email)) {
    errors.email = 'Please enter a valid email address';
  }
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
}

const flightDataTransformer = {
  transformPNRToFlightData,
  validateCustomerData,
  getCityNameFromAirportCode
};
export default flightDataTransformer;

